const SHADOW_OPACITY = 0.6;
const VERTICAL_SHADOW_OPACITY = SHADOW_OPACITY + 0.1;
const INITIAL_DELAY = 200;

const waitForAnimation = async (element, className) => {
  return new Promise((resolve) => {
    element.classList.add(className);
    element.addEventListener(
      "animationend",
      () => {
        element.classList.remove(className);
        resolve();
      },
      { once: true }
    );
  });
};

/* "visible" meaning "scrolled into viewport" */
const waitUntilVisible = (e, callback) => {
  /* Guard against divide-by-zero */
  const scrollHeight = e.scrollHeight > 0 ? e.scrollHeight : 1;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.some((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(e);
          callback();
          return true;
        }
      });
    },
    {
      root: document,
      /* 100px from viewport to make sure the arrow is visible, if < 100px, just half */
      threshold: scrollHeight > 100.0 ? 100.0 / scrollHeight : 0.5,
    }
  );

  observer.observe(e);
};

const isAlreadyAttached = (container) => {
  const selector = ":scope > .scroll-hint-shadow, :scope > .scroll-hint-shadow-vertical";
  return !!(container.querySelector(selector) || container.parentElement?.querySelector(selector));
};

const decideApproach = (container, { hasHorizontal, hasVertical }) => {
  // For vertical-only, anchor shadows to the parent (non-scrolling) so they don't move with content
  if (!hasHorizontal && hasVertical) {
    return {
      isWrapperMode: false,
      scrollContainer: container,
      shadowContainer: container.parentElement || container,
    };
  }

  const childTableContainer = container.querySelector(":scope > .table-container");
  if (childTableContainer) {
    return {
      isWrapperMode: false,
      scrollContainer: childTableContainer,
      shadowContainer: childTableContainer.parentElement,
    };
  } else {
    return {
      isWrapperMode: true,
      scrollContainer: container,
      shadowContainer: null,
    };
  }
};

const createShadows = ({
  isWrapperMode,
  container,
  shadowContainer,
  hasHorizontal,
  hasVertical,
}) => {
  let finalShadowContainer = shadowContainer;

  if (isWrapperMode) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("scroll-hint-wrapper");
    container.parentNode.insertBefore(wrapper, container);
    wrapper.appendChild(container);
    finalShadowContainer = wrapper;
  }

  if (!finalShadowContainer) return null;

  if (window.getComputedStyle(finalShadowContainer).position === "static") {
    finalShadowContainer.style.position = "relative";
  }

  const shadows = { shadowContainer: finalShadowContainer };

  if (hasHorizontal) {
    const leftShadow = document.createElement("div");
    leftShadow.className = "scroll-hint-shadow left";
    leftShadow.style.opacity = "0";
    leftShadow.style.pointerEvents = "none";

    const rightShadow = document.createElement("div");
    rightShadow.className = "scroll-hint-shadow right";
    rightShadow.style.opacity = "0";
    rightShadow.style.pointerEvents = "none";

    finalShadowContainer.appendChild(leftShadow);
    finalShadowContainer.appendChild(rightShadow);
    shadows.leftShadow = leftShadow;
    shadows.rightShadow = rightShadow;
  }

  if (hasVertical) {
    const topShadow = document.createElement("div");
    topShadow.className = "scroll-hint-shadow-vertical top";
    topShadow.style.pointerEvents = "none";
    topShadow.style.opacity = "0";
    const bottomShadow = document.createElement("div");
    bottomShadow.className = "scroll-hint-shadow-vertical bottom";
    bottomShadow.style.pointerEvents = "none";
    bottomShadow.style.opacity = "0";

    finalShadowContainer.appendChild(topShadow);
    finalShadowContainer.appendChild(bottomShadow);
    shadows.topShadow = topShadow;
    shadows.bottomShadow = bottomShadow;
  }

  return shadows;
};

const commonShadowSetup = ({ scrollContainer, leftShadow, rightShadow, calcScrollDistance }) => {
  const updateVisibility = () => {
    if (!document.contains(scrollContainer)) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const maxScroll = scrollWidth - clientWidth;

    const leftVisible = scrollLeft > 5;
    leftShadow.style.opacity = leftVisible ? String(SHADOW_OPACITY) : "0";
    leftShadow.style.pointerEvents = leftVisible ? "auto" : "none";

    const rightVisible = maxScroll > 5 && scrollLeft < maxScroll - 5;
    rightShadow.style.opacity = rightVisible ? String(SHADOW_OPACITY) : "0";
    rightShadow.style.pointerEvents = rightVisible ? "auto" : "none";
  };

  const updateHeights = () => {
    if (!document.contains(scrollContainer)) return;
    const h = scrollContainer.offsetHeight;
    leftShadow.style.height = h + "px";
    rightShadow.style.height = h + "px";
  };

  const updateHeightsAndVisibility = () => {
    updateHeights();
    updateVisibility();
  };

  const handleLeftClick = () =>
    scrollContainer.scrollBy({ left: -calcScrollDistance(), behavior: "smooth" });
  const handleRightClick = () =>
    scrollContainer.scrollBy({ left: calcScrollDistance(), behavior: "smooth" });

  scrollContainer.addEventListener("scroll", updateVisibility);
  window.addEventListener("resize", updateHeightsAndVisibility);
  leftShadow.addEventListener("click", handleLeftClick);
  rightShadow.addEventListener("click", handleRightClick);

  updateHeightsAndVisibility();

  waitUntilVisible(rightShadow, async () => {
    if (document.contains(rightShadow)) {
      await waitForAnimation(rightShadow, "pulse-shadow");
    }
  });
};

const verticalShadowSetup = ({
  scrollContainer,
  shadowContainer,
  topShadow,
  bottomShadow,
  bottomOffset = 0,
}) => {
  // Ensure the shadow container is the positioning context
  const shadowStyle = window.getComputedStyle(shadowContainer);
  if (shadowStyle.position === "static") {
    shadowContainer.style.position = "relative";
  }

  // Find sticky footer if any
  const getStickyFooterHeight = () => {
    // Look inside the scroll container or shadow container for the marker
    const stickyFooter =
      scrollContainer.querySelector("[data-sticky-footer]") ||
      shadowContainer.querySelector("[data-sticky-footer]");
    return stickyFooter ? stickyFooter.offsetHeight : 0;
  };

  const updatePositions = () => {
    if (!document.contains(scrollContainer) || !document.contains(shadowContainer)) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const shadowRect = shadowContainer.getBoundingClientRect();

    // Account for borders on the shadowContainer (offsetParent)
    // absolute positioning is relative to the padding box of the offsetParent.
    // getBoundingClientRect is relative to viewport (including borders).
    // So we need to subtract the border width of the shadowContainer.
    const borderTop = parseFloat(shadowStyle.borderTopWidth) || 0;
    const borderLeft = parseFloat(shadowStyle.borderLeftWidth) || 0;
    const borderBottom = parseFloat(shadowStyle.borderBottomWidth) || 0;

    // Relative top/left from shadowContainer's padding box origin
    const relTop = containerRect.top - shadowRect.top - borderTop;
    const relLeft = containerRect.left - shadowRect.left - borderLeft;

    // Distance from bottom of shadowContainer's padding box to bottom of scrollContainer
    const distFromBottom = shadowRect.bottom - borderBottom - containerRect.bottom;

    const stickyFooterHeight = getStickyFooterHeight();
    const totalBottomOffset = distFromBottom + bottomOffset + stickyFooterHeight;

    topShadow.style.top = `${relTop}px`;
    topShadow.style.left = `${relLeft}px`;
    topShadow.style.width = `${containerRect.width}px`;

    bottomShadow.style.bottom = `${totalBottomOffset}px`;
    bottomShadow.style.left = `${relLeft}px`;
    bottomShadow.style.width = `${containerRect.width}px`;
  };

  const updateVisibility = () => {
    if (!document.contains(scrollContainer)) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const maxScroll = scrollHeight - clientHeight;

    const topVisible = scrollTop > 5;
    topShadow.style.opacity = topVisible ? String(VERTICAL_SHADOW_OPACITY) : "0";
    topShadow.style.pointerEvents = topVisible ? "auto" : "none";
    topShadow.style.cursor = topVisible ? "pointer" : "default";

    const bottomVisible = maxScroll > 5 && scrollTop < maxScroll - 5;
    bottomShadow.style.opacity = bottomVisible ? String(VERTICAL_SHADOW_OPACITY) : "0";
    bottomShadow.style.pointerEvents = bottomVisible ? "auto" : "none";
    bottomShadow.style.cursor = bottomVisible ? "pointer" : "default";
  };

  const handleResize = () => {
    updatePositions();
    updateVisibility();
  };

  const handleTopClick = () => {
    scrollContainer.scrollBy({
      top: -Math.floor(scrollContainer.clientHeight * 0.75),
      behavior: "smooth",
    });
  };

  const handleBottomClick = () => {
    scrollContainer.scrollBy({
      top: Math.floor(scrollContainer.clientHeight * 0.75),
      behavior: "smooth",
    });
  };

  scrollContainer.addEventListener("scroll", updateVisibility);
  window.addEventListener("resize", handleResize);
  topShadow.addEventListener("click", handleTopClick);
  bottomShadow.addEventListener("click", handleBottomClick);

  // Use ResizeObserver to detect size changes in scrollContainer, shadowContainer, or sticky footer
  const observer = new ResizeObserver(() => {
    handleResize();
  });
  observer.observe(scrollContainer);
  observer.observe(shadowContainer);

  const stickyFooter =
    scrollContainer.querySelector("[data-sticky-footer]") ||
    shadowContainer.querySelector("[data-sticky-footer]");
  if (stickyFooter) {
    observer.observe(stickyFooter);
  }

  waitUntilVisible(bottomShadow, async () => {
    setTimeout(async () => {
      if (document.contains(bottomShadow)) {
        await waitForAnimation(bottomShadow, "pulse-shadow-vertical");
      }
    }, 250);
  });

  // Initial setup
  requestAnimationFrame(() => {
    handleResize();
  });
};

const setupSingleContainer = (container) => {
  const hasHorizontal = container.hasAttribute("data-scroll-hint");
  const hasVertical = container.hasAttribute("data-scroll-hint-vertical");
  if (!hasHorizontal && !hasVertical) return null;
  if (isAlreadyAttached(container)) return null;

  const { isWrapperMode, scrollContainer, shadowContainer } = decideApproach(container, {
    hasHorizontal,
    hasVertical,
  });

  const result = createShadows({
    isWrapperMode,
    container,
    shadowContainer,
    hasHorizontal,
    hasVertical,
  });
  if (!result) return null;

  const { leftShadow, rightShadow, topShadow, bottomShadow } = result;
  let finalShadowContainer = result.shadowContainer;

  // detect if there's a sticky first column (like on Nutrition Breakdown)
  let stickyColWidth = 0;
  let hasStickyFirstColumn = false;

  const table = scrollContainer.querySelector("table");
  if (table) {
    const firstCol = table.querySelector("th:first-child, td:first-child");
    if (firstCol && window.getComputedStyle(firstCol).position === "sticky") {
      hasStickyFirstColumn = true;
      stickyColWidth = firstCol.offsetWidth;
      leftShadow.style.left = `${stickyColWidth}px`;
    }
  }

  let calcScrollDistance;
  if (hasStickyFirstColumn) {
    calcScrollDistance = () => {
      const visibleWidth = scrollContainer.clientWidth - stickyColWidth;
      return Math.floor(visibleWidth * 0.75);
    };
  } else {
    calcScrollDistance = () => {
      return Math.floor(scrollContainer.clientWidth * 0.75);
    };
  }

  if (hasHorizontal && leftShadow && rightShadow) {
    commonShadowSetup({
      scrollContainer,
      leftShadow,
      rightShadow,
      calcScrollDistance,
    });
  }

  if (hasVertical && topShadow && bottomShadow) {
    const bottomOffset = parseInt(container.dataset.scrollHintVerticalOffsetBottom || "0", 10);
    verticalShadowSetup({
      scrollContainer,
      shadowContainer: finalShadowContainer,
      topShadow,
      bottomShadow,
      bottomOffset,
    });
  }

  const handleResizeOffset = () => {
    if (!document.contains(scrollContainer)) return;
    if (hasStickyFirstColumn && table) {
      const firstCol = table.querySelector("th:first-child, td:first-child");
      if (firstCol) {
        stickyColWidth = firstCol.offsetWidth;
        leftShadow.style.left = `${stickyColWidth}px`;
      }
    }
  };
  window.addEventListener("resize", handleResizeOffset);
};

const scan = () => {
  document
    .querySelectorAll("[data-scroll-hint], [data-scroll-hint-vertical]")
    .forEach(setupSingleContainer);
};

document.addEventListener("DOMContentLoaded", () => {
  setTimeout(scan, INITIAL_DELAY);
});
document.addEventListener("partial:replace", () => {
  setTimeout(scan, INITIAL_DELAY);
});
document.addEventListener("scroll-hint:scan", () => {
  setTimeout(scan, INITIAL_DELAY);
});
